import * as React from 'react';
import { FC, useMemo, useState } from 'react';
import { Button, Popconfirm, Popover } from 'antd';
import {
	checkHasApprovalHierarchy,
	compareStrings,
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { AWARD_STATUSES } from '../../constants/BidderStatuses';
import {
	buildApprovalTierView,
	checkIfWillFullyApprovedUtil,
	getApprovalActionGate,
	getUserApprovalTier,
	highestApprovalTierAmount,
} from '../../utils/ApprovalHierarchyUtils';
import ApprovalHierarchyDisplayQuoteModal from '../approval_hierarchy_display_quote/ApprovalHierarchyDisplayQuoteModal';
import {
	isAllowedToPerform,
	isSupplierAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

/**
 * Award/Decline controls for a single RFP bidder, gated by the RFP's approval hierarchy.
 *
 * Mirrors the quote approve/decline gating (see ApprovalHierarchyDisplayQuote): the current
 * user may only act when their approval tier covers the bidder's bid total, sequential
 * enforcement allows it, and their tier hasn't already approved. Disabled buttons explain
 * why via a Popover. The approvers tier-status modal is shared with the quote display.
 *
 * Only rendered when the RFP has an approval hierarchy; otherwise BidderCard keeps its
 * original ungated buttons.
 */
const ApprovalHierarchyDisplayRFPAward: FC<any> = ({
	requestForProposal,
	bidder,
	bidderAwarded,
	awardUpdating,
	handleAwardStatusChange,
	currentUser,
	companyConfig,
	userType,
}) => {
	const [showApproversModal, setShowApproversModal] = useState(false);

	// No-hierarchy fallback: gate on the same approve permission the quote path uses so a view-only
	// user can't award/decline a bid.
	const defaultActionsAllowed =
		userType === ROLE_TYPES.SUPPLIER
			? isSupplierAllowedToAccess(
					PERMISSION_NAMES.CAN_APPROVE_QUOTES,
					companyConfig,
					currentUser.roles
			  )
			: isAllowedToPerform(PERMISSION_NAMES.CAN_APPROVE_QUOTES, companyConfig, currentUser.roles);

	// The approval hierarchy + per-bidder approval state live on the bidder's proposal (the backend
	// routes each RFP bid through an RFP-typed hierarchy on the proposal, not on the RFP entity).
	const proposal = nullSafeGet('proposal', bidder);
	const hasApprovalHierarchy = checkHasApprovalHierarchy(proposal);
	const sequentialApprovalEnforcement = nullSafeGetOrElse(
		'approvalHierarchy.sequentialApprovalEnforcement',
		proposal,
		false
	);
	const currency = getCurrency({ supplierFacility: nullSafeGet('supplierFacility', bidder) });
	const bidTotal = nullSafeGetOrElse('proposal.totalAfterTax', bidder, 0);
	const supplierName = nullSafeGetOrElse(
		'supplierFacility.name',
		bidder,
		nullSafeGetOrElse('supplierFacility.displayName', bidder, 'this supplier')
	);
	const awardConfirmTitle = (
		<div style={{ maxWidth: 355, whiteSpace: 'normal' }}>
			{`${supplierName} will be awarded this RFP and notified. All other proposals will be declined.`}
		</div>
	);

	const { tiers, isNTEBased } = useMemo(
		() =>
			buildApprovalTierView(nullSafeGetOrElse('approvalHierarchy.tierDetails', proposal, []), {
				amount: bidTotal,
				exchangeRate: 1,
				currency,
			}),
		[proposal, bidTotal, currency]
	);

	const myTier = useMemo(
		() => getUserApprovalTier(tiers, currentUser, hasApprovalHierarchy),
		[tiers, currentUser, hasApprovalHierarchy]
	);
	const gate = useMemo(
		() =>
			getApprovalActionGate({
				hasApprovalHierarchy,
				myTier,
				// Match the quote/invoice rule: gate on the highest tier that has ALREADY approved,
				// not the bid total. Approvers whose limit is below the bid amount can still approve
				// (the cascade just isn't finalized until a covering tier approves).
				requiredAmount: highestApprovalTierAmount(tiers),
				currentUser,
				sequentialApprovalEnforcement,
				tiers,
				defaultActionsAllowed,
			}),
		[
			hasApprovalHierarchy,
			myTier,
			currentUser,
			sequentialApprovalEnforcement,
			tiers,
			defaultActionsAllowed,
		]
	);

	// Would the current user's approval fully approve (award) this bid? With no hierarchy, awarding
	// finalizes directly. Otherwise simulate their approval landing on their tier and run the full
	// backend rule (covers the equal-tier guard). A full approval keeps the "Award" + confirm; a
	// partial one is a plain "Approve" that records their tier approval immediately.
	const willFullyApprove = useMemo(() => {
		if (!hasApprovalHierarchy) {
			return true;
		}
		if (!myTier) {
			return false;
		}
		const simulatedTiers = tiers.map((tier) =>
			tier === myTier
				? {
						...tier,
						approvedByUsers: [
							...nullSafeGetOrElse('approvedByUsers', tier, []),
							{ email: nullSafeGet('email', currentUser) },
						],
				  }
				: tier
		);
		return checkIfWillFullyApprovedUtil(bidTotal, simulatedTiers);
	}, [hasApprovalHierarchy, myTier, tiers, bidTotal, currentUser]);

	// Append the current user to their tier (approved/declined) so the backend can record the
	// Award/decline go through the per-bidder endpoints (PATCH /quote/bidders/:rfpId/:sfId/:status).
	// The backend records the calling approver (derived from auth) into the RFP's approval tiers and
	// resolves the final award — the FE does not send tier state in the body.
	const onAction = (action) =>
		handleAwardStatusChange(requestForProposal.id, bidder.supplierFacilityId, action);

	const renderActionButton = (action, label, buttonProps = {}) =>
		gate.allowed ? (
			<Popconfirm
				onConfirm={() => onAction(action)}
				placement="topLeft"
				title={
					action === AWARD_STATUSES.Awarded
						? awardConfirmTitle
						: 'Send declined note to the supplier?'
				}
				okText={action === AWARD_STATUSES.Awarded ? 'Confirm award' : 'Confirm decline'}
				cancelText="Cancel"
			>
				<Button loading={awardUpdating} {...buttonProps}>
					{label}
				</Button>
			</Popconfirm>
		) : (
			<Popover
				title={`Why can't I ${action === AWARD_STATUSES.Awarded ? 'award' : 'decline'}?`}
				content={gate.reason}
			>
				<Button disabled={true} {...buttonProps}>
					{label}
				</Button>
			</Popover>
		);

	// Award/Approve button. A full approval ("Award") confirms first and notifies the supplier; a
	// partial approval ("Approve") just records the current user's tier approval and fires on click.
	const renderApproveButton = () => {
		const buttonProps: any = { type: 'primary' };
		const label = willFullyApprove ? 'Award' : 'Approve';
		if (!gate.allowed) {
			return (
				<Popover title="Why can't I approve?" content={gate.reason}>
					<Button disabled={true} {...buttonProps}>
						{label}
					</Button>
				</Popover>
			);
		}
		if (willFullyApprove) {
			return (
				<Popconfirm
					onConfirm={() => onAction(AWARD_STATUSES.Awarded)}
					placement="topLeft"
					title={awardConfirmTitle}
					okText="Confirm award"
					cancelText="Cancel"
				>
					<Button loading={awardUpdating} {...buttonProps}>
						{label}
					</Button>
				</Popconfirm>
			);
		}
		return (
			<Button
				loading={awardUpdating}
				{...buttonProps}
				onClick={() => onAction(AWARD_STATUSES.Awarded)}
			>
				{label}
			</Button>
		);
	};

	const approversLink = (
		<div
			className={'inline-flex flex-row items-center'}
			style={{ cursor: 'pointer', fontSize: '14px' }}
			onClick={() => setShowApproversModal(true)}
		>
			<i className="icons8-font icons8-multiple-users" />
			<span style={{ marginLeft: '8px', textDecoration: 'underline' }}>Approvers</span>
		</div>
	);

	const awardStatus = nullSafeGet('awardStatus', bidder);

	return (
		<div>
			<ApprovalHierarchyDisplayQuoteModal
				isNTEBased={isNTEBased}
				tiers={tiers}
				hasApprovalHierarchy={hasApprovalHierarchy}
				currentQuote={proposal}
				setVisible={setShowApproversModal}
				visible={showApproversModal}
				style={{}}
				exchangeRate={1}
				currency={currency}
				currentUser={currentUser}
				refresh={() => {}}
				syncQuote={() => Promise.resolve()}
			/>
			{compareStrings(awardStatus, AWARD_STATUSES.Awarded) ? (
				<div className="flex flex-row items-center gap-2">
					<div style={{ fontSize: 16 }}>Awarded</div>
					{approversLink}
				</div>
			) : compareStrings(awardStatus, AWARD_STATUSES.Declined) ? (
				<div className="flex flex-row items-center gap-2">
					<div style={{ fontSize: 16 }}>Declined</div>
					{approversLink}
				</div>
			) : bidderAwarded ? (
				<div className="flex flex-row items-center gap-2">
					{renderActionButton(AWARD_STATUSES.Declined, 'Decline', { type: 'primary' })}
					{approversLink}
				</div>
			) : (
				<div className="flex flex-row items-center gap-2">
					{renderApproveButton()}
					{renderActionButton(AWARD_STATUSES.Declined, 'Decline', {
						type: 'ghost',
						style: { backgroundColor: 'white' },
					})}
					{approversLink}
				</div>
			)}
		</div>
	);
};

export default ApprovalHierarchyDisplayRFPAward;
